import { useState, useRef, useEffect } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import classes from "./cascadingDropdown.module.css";

function getChildren(items, parentId) {
  return items.filter((item) => item.parent_id === parentId);
}

function buildSelectionChain(items, initialIds) {
  if (!initialIds) return [null];

  const { main_category_id, category_id, sub_category_id } = initialIds;
  const chain = [];

  const root = items.find(
    (i) => i.id === main_category_id && i.parent_id === null,
  );
  if (!root) return [null];
  chain.push(root.id);

  const mid = items.find(
    (i) => i.id === category_id && i.parent_id === main_category_id,
  );
  if (!mid) return chain;
  chain.push(mid.id);

  const leaf = items.find(
    (i) => i.id === sub_category_id && i.parent_id === category_id,
  );
  if (leaf) chain.push(leaf.id);

  return chain;
}

function Dropdown({ label, options, value, onChange, disabled, error }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const containerState = error ? classes.error : value ? classes.selected : "";
  const labelState = error
    ? classes.labelError
    : value
      ? classes.labelActive
      : "";

  const selected = options.find((o) => o.value === value);

  return (
    <div className={classes.wrapper} ref={ref}>
      <div
        className={`${classes.dropdownBox} ${containerState} ${
          disabled ? classes.disabled : ""
        }`}
        onClick={() => !disabled && setOpen((p) => !p)}
      >
        <div className={classes.displayText}>
          {selected ? selected.label : ""}
        </div>

        <label className={`${classes.floatingLabel} ${labelState}`}>
          {label}
          <span className={classes.asterisk}>*</span>
        </label>

        <span className={classes.arrow}>{open ? "▲" : "▼"}</span>

        {error && typeof error === "string" && (
          <div className={classes.errorIcon} title={error}>
            <ExclamationCircleIcon className={classes.icon} />
          </div>
        )}
      </div>

      {open && !disabled && (
        <ul className={classes.optionsList}>
          {options.length === 0 ? (
            <li className={classes.optionEmpty}>No options available</li>
          ) : (
            options.map((opt) => (
              <li
                key={opt.value}
                className={`${classes.option} ${
                  opt.value === value ? classes.selectedOption : ""
                }`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export function CascadingDropdowns({
  items = [],
  onChange,
  setValue,
  name = "category_id",
  error,
  initialIds = null,
}) {
  const [selections, setSelections] = useState(() =>
    buildSelectionChain(items, initialIds),
  );

  useEffect(() => {
    setSelections(buildSelectionChain(items, initialIds));
  }, [
    items,
    initialIds?.main_category_id,
    initialIds?.category_id,
    initialIds?.sub_category_id,
  ]);

  const handleSelect = (levelIndex, selectedId) => {
    const next = selections.slice(0, levelIndex + 1);
    next[levelIndex] = selectedId;
    setSelections(next);

    const selectedItem = items.find((i) => i.id === selectedId);
    const children = getChildren(items, selectedId);
    const isLeaf = children.length === 0;

    if (isLeaf) {
      onChange?.(selectedItem);
      setValue?.(name, selectedId, { shouldValidate: true });
    } else {
      onChange?.(null);
      setValue?.(name, null, { shouldValidate: false });
    }
  };

  const levels = [];

  const roots = items.filter((i) => i.parent_id === null);
  if (roots.length) {
    levels.push({
      label: "Main Category",
      options: roots.map((i) => ({ value: i.id, label: i.name })),
      levelIndex: 0,
    });
  }

  selections.forEach((selectedId, idx) => {
    if (selectedId == null) return;
    const selectedItem = items.find((i) => i.id === selectedId);
    if (!selectedItem) return;
    const children = getChildren(items, selectedId);
    if (children.length === 0) return;

    levels.push({
      label: selectedItem.level === 1 ? "Category" : "Sub Category",
      options: children.map((i) => ({ value: i.id, label: i.name })),
      levelIndex: idx + 1,
    });
  });

  const lastIndex = levels.length - 1;

  return (
    <div className={classes.container}>
      <div className={classes.dropdownRow}>
        {levels.map(({ label, options, levelIndex }) => (
          <Dropdown
            key={levelIndex}
            label={label}
            options={options}
            value={selections[levelIndex] ?? null}
            onChange={(id) => handleSelect(levelIndex, id)}
            error={levelIndex === lastIndex ? error : undefined}
          />
        ))}
      </div>

      {error && <p className={classes.errorText}>{error}</p>}
    </div>
  );
}